import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { SSOAttribute, SSOAttributeVisibilityRole } from '../types';
import { oktaAPI } from '../services/api';
import {
  SSO_ATTRIBUTE_VISIBILITY_OPTIONS,
  formatVisibilityRoles,
  getVisibilityRoleLabels,
} from '../constants/ssoAttributeVisibility';
import { PAGE } from '../constants/pageStyles';
import { UI_TOKENS } from '../constants/uiTokens';

interface SSOAttributeSettingsProps {
  userRole: string;
}

const extractApiError = (err: unknown, fallback: string): string => {
  const ax = err as { response?: { data?: { message?: string; error?: string } } };
  return ax?.response?.data?.message || ax?.response?.data?.error || fallback;
};

const formatAttributeSource = (attribute: SSOAttribute): { label: string; color: 'info' | 'primary' | 'default' } => {
  if (attribute.source === 'okta') {
    return {
      label: attribute.source_app_name ? `Okta · ${attribute.source_app_name}` : 'Okta',
      color: 'info',
    };
  }
  if (attribute.source === 'azure') {
    return {
      label: attribute.source_app_name ? `Entra · ${attribute.source_app_name}` : 'Microsoft Entra ID',
      color: 'primary',
    };
  }
  return { label: 'Manual', color: 'default' };
};

const SSOAttributeSettings: React.FC<SSOAttributeSettingsProps> = ({ userRole }) => {
  const [attributes, setAttributes] = useState<SSOAttribute[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<SSOAttribute | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    is_standard: false,
    is_active: true,
    visible_to_roles: ['app_admin', 'user', 'delegate', 'sso_admin', 'super_admin'] as SSOAttributeVisibilityRole[],
  });

  const canManageAttributes = ['super_admin', 'sso_admin'].includes(userRole);

  const loadAttributes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await oktaAPI.listSsoAttributes('management');
      setAttributes(res.data?.data || []);
    } catch (err) {
      setError(extractApiError(err, 'Failed to load attributes'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManageAttributes) {
      void loadAttributes();
    }
  }, [canManageAttributes, loadAttributes]);

  const handleSyncAttributes = async () => {
    setSyncing(true);
    setError('');
    try {
      const res = await oktaAPI.syncSsoAttributes();
      const payload = res.data?.data;
      setAttributes(payload?.attributes || []);
      setSuccess(res.data?.message || `Synced ${payload?.synced ?? 0} attribute(s) from configured identity providers`);
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      setError(extractApiError(err, 'Failed to sync attributes. Configure Okta or Microsoft Entra ID under Security → SSO.'));
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenDialog = (attribute?: SSOAttribute) => {
    if (attribute) {
      setEditingAttribute(attribute);
      setFormData({
        name: attribute.name,
        display_name: attribute.display_name,
        description: attribute.description || '',
        is_standard: attribute.is_standard,
        is_active: attribute.is_active,
        visible_to_roles: attribute.visible_to_roles?.length
          ? [...attribute.visible_to_roles]
          : ['all'],
      });
    } else {
      setEditingAttribute(null);
      setFormData({
        name: '',
        display_name: '',
        description: '',
        is_standard: false,
        is_active: true,
        visible_to_roles: ['app_admin', 'user', 'delegate', 'sso_admin', 'super_admin'],
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAttribute(null);
  };

  const handleSaveAttribute = async () => {
    if (!formData.name || !formData.display_name) {
      setError('Name and Display Name are required');
      return;
    }
    if (!formData.visible_to_roles.length) {
      setError('Select at least one visibility audience');
      return;
    }

    try {
      if (editingAttribute) {
        const res = await oktaAPI.updateSsoAttribute(editingAttribute.id, {
          display_name: formData.display_name,
          description: formData.description,
          is_active: formData.is_active,
          visible_to_roles: formData.visible_to_roles,
        });
        const updated = res.data?.data;
        if (updated) {
          setAttributes((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
        } else {
          await loadAttributes();
        }
        setSuccess('Attribute updated successfully');
      } else {
        const res = await oktaAPI.createSsoAttribute({
          name: formData.name,
          display_name: formData.display_name,
          description: formData.description,
          is_standard: formData.is_standard,
          is_active: formData.is_active,
          visible_to_roles: formData.visible_to_roles,
        });
        const created = res.data?.data;
        if (created) {
          setAttributes((prev) => [...prev, created]);
        } else {
          await loadAttributes();
        }
        setSuccess('Attribute created successfully');
      }
      handleCloseDialog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(extractApiError(err, 'Failed to save attribute'));
    }
  };

  const handleDeleteAttribute = async (attributeId: string) => {
    if (!window.confirm('Are you sure you want to delete this attribute?')) return;
    try {
      await oktaAPI.deleteSsoAttribute(attributeId);
      setAttributes((prev) => prev.filter((a) => a.id !== attributeId));
      setSuccess('Attribute deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(extractApiError(err, 'Failed to delete attribute'));
    }
  };

  const handleToggleActive = async (attribute: SSOAttribute) => {
    try {
      const res = await oktaAPI.updateSsoAttribute(attribute.id, { is_active: !attribute.is_active });
      const updated = res.data?.data;
      if (updated) {
        setAttributes((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      }
      setSuccess('Attribute status updated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(extractApiError(err, 'Failed to update attribute status'));
    }
  };

  if (!canManageAttributes) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don&apos;t have permission to manage SSO attributes. Only Super Admins and SSO Admins can access this feature.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={PAGE.subsectionTitle}>SSO Attribute Management</Typography>
          <Typography sx={PAGE.helper}>
            Sync user profile and SAML attributes from all configured identity providers (Okta and Microsoft Entra ID).
            Each attribute is tagged with its source app.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={18} /> : <SyncIcon />}
            onClick={() => void handleSyncAttributes()}
            disabled={syncing || loading}
          >
            Sync attributes
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add Custom Attribute
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2, maxHeight: 'min(70vh, 640px)', overflow: 'auto' }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 960 }}>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'text.secondary',
                  bgcolor: 'grey.50',
                  py: 1.25,
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <TableCell sx={{ width: '14%' }}>Name</TableCell>
              <TableCell sx={{ width: '14%' }}>Display name</TableCell>
              <TableCell sx={{ width: '22%' }}>Description</TableCell>
              <TableCell sx={{ width: '9%' }}>Type</TableCell>
              <TableCell sx={{ width: '14%' }}>Visibility</TableCell>
              <TableCell sx={{ width: '8%' }}>Source</TableCell>
              <TableCell sx={{ width: '9%', textAlign: 'center' }}>Status</TableCell>
              <TableCell sx={{ width: '10%', textAlign: 'center' }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : attributes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No attributes are saved yet. A sample mapping releases email as user.email. Opening it does not save the attribute.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setEditingAttribute(null);
                      setFormData({
                        name: 'email',
                        display_name: 'user.email',
                        description: 'Sample attribute. Email is released as user.email. Nothing is saved until you save.',
                        is_standard: true,
                        is_active: true,
                        visible_to_roles: ['app_admin', 'user', 'delegate', 'sso_admin', 'super_admin'],
                      });
                      setDialogOpen(true);
                    }}
                  >
                    Open the email sample
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              attributes.map((attribute, idx) => {
                const visibilityLabels = getVisibilityRoleLabels(attribute.visible_to_roles);
                const sourceMeta = formatAttributeSource(attribute);
                return (
                <TableRow
                  key={attribute.id}
                  hover
                  sx={{
                    bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                    '& td': { py: 1, verticalAlign: 'middle' },
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      fontSize="0.8rem"
                      noWrap
                      title={attribute.name}
                    >
                      {attribute.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500} noWrap title={attribute.display_name}>
                      {attribute.display_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {attribute.description ? (
                      <Tooltip title={attribute.description}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.8rem',
                          }}
                        >
                          {attribute.description}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.disabled" fontSize="0.8rem">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={attribute.is_standard ? 'Standard' : 'Custom'}
                      size="small"
                      variant="outlined"
                      color={attribute.is_standard ? 'primary' : 'default'}
                      sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                      {visibilityLabels.length === 0 ? (
                        <Typography variant="body2" color="text.disabled" fontSize="0.8rem">
                          —
                        </Typography>
                      ) : (
                        visibilityLabels.map((label) => (
                          <Chip
                            key={`${attribute.id}-${label}`}
                            label={label}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontSize: '0.68rem',
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        ))
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={attribute.source_app_name || sourceMeta.label}>
                      <Chip
                        label={sourceMeta.label}
                        size="small"
                        variant="outlined"
                        color={sourceMeta.color}
                        sx={{ height: 22, fontSize: '0.68rem', maxWidth: '100%' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={attribute.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'}>
                      <Switch
                        size="small"
                        checked={attribute.is_active}
                        onChange={() => void handleToggleActive(attribute)}
                        inputProps={{ 'aria-label': `${attribute.display_name} active` }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenDialog(attribute)} aria-label="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          attribute.is_standard
                            ? 'Standard attributes cannot be deleted'
                            : 'Delete'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => void handleDeleteAttribute(attribute.id)}
                            disabled={attribute.is_standard}
                            color="error"
                            aria-label="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingAttribute ? 'Edit Attribute' : 'Add Custom Attribute'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Attribute Name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                helperText="Internal name (lowercase, no spaces)"
                disabled={Boolean(editingAttribute)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display Name"
                value={formData.display_name}
                onChange={(e) => setFormData((prev) => ({ ...prev, display_name: e.target.value }))}
                helperText="User-friendly name shown in forms"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Visible to (application setup)</InputLabel>
                <Select
                  multiple
                  value={formData.visible_to_roles}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      visible_to_roles: (typeof value === 'string' ? value.split(',') : value) as SSOAttributeVisibilityRole[],
                    }));
                  }}
                  input={<OutlinedInput label="Visible to (application setup)" />}
                  renderValue={(selected) => formatVisibilityRoles(selected as string[])}
                >
                  {SSO_ATTRIBUTE_VISIBILITY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Checkbox checked={formData.visible_to_roles.indexOf(opt.value) > -1} />
                      <ListItemText primary={opt.label} secondary={opt.description} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {!editingAttribute && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.is_standard ? 'standard' : 'custom'}
                    label="Type"
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, is_standard: e.target.value === 'standard' }))
                    }
                  >
                    <MenuItem value="custom">Custom</MenuItem>
                    <MenuItem value="standard">Standard</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button onClick={() => void handleSaveAttribute()} variant="contained" startIcon={<SaveIcon />}>
            {editingAttribute ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SSOAttributeSettings;
